import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { apiErr } from "../lib/api";
import { Logo } from "../components/Logo";
import { StatusBadge } from "../components/StatusBadge";
import { STATUS_META } from "../lib/format";
import { Search, MapPin, PackageX, CheckCircle2, Circle, Clock, Zap } from "lucide-react";

export default function TrackPublic() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(code || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchTrack = async (c) => {
    if (!c) return;
    setLoading(true); setError(""); setData(null);
    try { const { data } = await api.get(`/track/${c.toUpperCase()}`); setData(data); }
    catch (e) { setError(apiErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (code) fetchTrack(code); }, [code]);

  const submit = (e) => { e.preventDefault(); if (input.trim()) navigate(`/track/${input.trim().toUpperCase()}`); };

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      <div className="bg-[#0A1F44] text-white">
        <div className="max-w-3xl mx-auto px-4 py-5 flex items-center justify-between">
          <Logo />
          <Link to="/login" className="text-sm font-medium text-slate-300 hover:text-white">Ingresar</Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-6 sm:p-8">
          <h1 className="text-2xl font-bold text-[#0A1F44] font-display">Seguimiento de envío</h1>
          <p className="text-sm text-[#64748B] mt-1">Ingresá tu código para ver el estado en tiempo real.</p>
          <form onSubmit={submit} className="mt-5 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="PY-000001" data-testid="public-track-input"
                className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2.5 text-sm font-mono outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-blue-100" />
            </div>
            <button className="rounded-lg bg-[#0A1F44] hover:bg-[#132D5C] text-white font-semibold px-5 text-sm" data-testid="public-track-btn">Rastrear</button>
          </form>

          {loading && <p className="mt-6 text-center text-[#64748B]">Buscando...</p>}
          {error && (
            <div className="mt-6 text-center py-8">
              <PackageX className="h-10 w-10 mx-auto text-rose-300" />
              <p className="mt-2 text-rose-600 font-medium" data-testid="track-error">{error}</p>
            </div>
          )}

          {data && (
            <div className="mt-6 fade-up">
              <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-slate-100">
                <div>
                  <div className="font-mono font-bold text-lg text-[#0A1F44]">{data.code}</div>
                  <div className="text-sm text-[#64748B]">Para {data.destinatario} · {data.ciudad}</div>
                </div>
                <StatusBadge status={data.status} />
              </div>

              <div className="grid grid-cols-2 gap-3 my-4">
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                  <div className="text-xs text-[#64748B] flex items-center gap-1"><MapPin className="h-3 w-3" /> Origen</div>
                  <div className="font-semibold text-[#0A1F44] text-sm mt-0.5">{data.sucursal_origen?.name || "—"}</div>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                  <div className="text-xs text-[#64748B] flex items-center gap-1"><MapPin className="h-3 w-3" /> Destino</div>
                  <div className="font-semibold text-[#0A1F44] text-sm mt-0.5">{data.sucursal_destino?.name || data.ciudad}</div>
                </div>
              </div>

              {data.es_punto_destino ? (
                ["en_sucursal_destino", "asignado_repartidor", "en_reparto"].includes(data.status) && (
                  <div className="mt-4 rounded-lg bg-violet-50 border border-violet-200 p-3.5 text-sm text-violet-900 flex gap-2.5 items-start" data-testid="track-aviso-punto">
                    <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-violet-500" />
                    <span>La entrega a domicilio no está disponible en esta zona. Tu encomienda te espera para <b>retiro en nuestro Punto Pya'elino de {data.ciudad}</b>.</span>
                  </div>
                )
              ) : (
                ["en_sucursal_destino", "asignado_repartidor", "en_reparto"].includes(data.status) && (
                  <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3.5 text-sm text-amber-900 flex gap-2.5 items-start" data-testid="track-aviso-horario">
                    <Clock className="h-4 w-4 mt-0.5 shrink-0 text-[#F5A623]" />
                    <span>Su entrega se realizará en destino en el transcurso del día; no es posible determinar un horario debido a las numerosas entregas.</span>
                  </div>
                )
              )}
              {data.tipo_servicio === "prioritario" && (
                <div className="mt-2 rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800 flex gap-2 items-center" data-testid="track-prioritario">
                  <Zap className="h-4 w-4 shrink-0 text-[#3B82F6]" />
                  <span>Este envío es <b>prioritario</b> y tiene preferencia de entrega.{data.horario_entrega ? ` Horario solicitado: ${data.horario_entrega}.` : ""}</span>
                </div>
              )}

              <h3 className="font-semibold text-[#0A1F44] mt-6 mb-3">Historial</h3>
              <ol className="relative border-l-2 border-slate-200 ml-2 space-y-5">
                {(data.events || []).map((ev, i) => (
                  <li key={i} className="ml-5" data-testid={`track-event-${i}`}>
                    <span className="absolute -left-[9px] flex items-center justify-center">
                      {i === (data.events || []).length - 1 ? <CheckCircle2 className="h-4 w-4 text-emerald-500 bg-white rounded-full" /> : <Circle className="h-3.5 w-3.5 fill-white" style={{ color: STATUS_META[ev.status]?.dot || "#94A3B8" }} />}
                    </span>
                    <div className="font-semibold text-sm text-[#0A1F44]">{ev.label}</div>
                    {ev.nota && <div className="text-xs text-[#64748B]">{ev.nota}</div>}
                    <div className="text-xs text-slate-400">{new Date(ev.at).toLocaleString("es-PY")}{ev.by ? ` · ${ev.by}` : ""}</div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
        <p className="text-center text-xs text-slate-400 mt-5">© {new Date().getFullYear()} Pya'elino Courier · Paraguay</p>
      </div>
    </div>
  );
}
